import { Loader, Text } from '@kubit-ui-web/react-components';
import { isValidRoomId } from '@point-ker/shared';
import { useEffect } from 'react';

import { HomeScreen } from '@/screens/HomeScreen';
import { RoomScreen } from '@/screens/RoomScreen';
import { useSocket } from '@/socket/SocketProvider';
import { useRoom } from '@/socket/useRoom';
import { AppFooter } from '@/ui/Footer';
import { Card, Page, Row } from '@/ui/layout';

/**
 * Screen selection, without a router.
 *
 * The app only ever has two views and the server already tells us which one
 * applies: holding a `RoomState` means you are in a room. Adding a routing
 * library to express one boolean would be weight for nothing.
 *
 * The room code still rides in the query string so a link can be shared, but
 * it is only ever an *input* — the URL follows the connection, never drives
 * it. That keeps the back button from appearing to teleport people between
 * rooms they are not actually joined to.
 */
export function App() {
  const { status } = useSocket();
  const room = useRoom();

  const initialRoomId = readRoomIdFromUrl();

  useEffect(() => {
    const url = new URL(window.location.href);
    if (room.state) url.searchParams.set('room', room.state.id);
    else url.searchParams.delete('room');
    window.history.replaceState(null, '', url.toString());
  }, [room.state?.id]);

  return (
    <Page>
      {status !== 'connected' && <ConnectionBanner connecting={status === 'connecting'} />}

      {room.state === null ? (
        <HomeScreen room={room} initialRoomId={initialRoomId} />
      ) : (
        <RoomScreen room={room} state={room.state} />
      )}

      <AppFooter />
    </Page>
  );
}

/**
 * Shown whenever the socket is not up. It matters more here than in most apps:
 * with the connection down the board is frozen rather than merely stale, and
 * silently showing the last state would let someone keep "voting" into
 * nothing.
 */
function ConnectionBanner({ connecting }: { connecting: boolean }) {
  return (
    <Card>
      <Row>
        <Loader variant="PRIMARY_BLACK" visible width="1.5rem" altText="Connecting" />
        <Text variant="PARAGRAPH_SMALL_EXPANDED" component="p">
          {connecting
            ? 'Connecting to the room server…'
            : 'Disconnected. Reconnecting — your seat is held for a minute.'}
        </Text>
      </Row>
    </Card>
  );
}

function readRoomIdFromUrl(): string {
  try {
    const candidate = new URL(window.location.href).searchParams.get('room')?.toUpperCase() ?? '';
    return isValidRoomId(candidate) ? candidate : '';
  } catch {
    return '';
  }
}
